import { Injectable } from "@angular/core";
import { GlobalsService } from "@services/utils/globals.service";
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from "@microsoft/signalr";
import { TeamChatStore, TextChatStore } from "@stores";
import { LoggerStore } from "@stores";
import { ITeamChatConversationSummaryModel, ITeamChatMessageModel, ITeamChatNotificationSettingsModel, ITextChatConversationSummaryModel, ITextChatMessageModel } from "@models";
import { debounceTime } from "rxjs/operators";

@Injectable()
export class TextChatHubService {
    private static isFirst: boolean = true;
    private static hubConnection: HubConnection = null;

    /** Long enough to swallow a slider drag, short enough that letting go feels immediate elsewhere. */
    private static readonly NOTIFICATION_SETTINGS_DEBOUNCE_MS = 250;

    constructor(private loggerStore: LoggerStore,
        private readonly textChatStore: TextChatStore,
        private readonly teamChatStore: TeamChatStore) {
    }

    private startConnection() {
        this.loggerStore.addLog(`TextChatHub start connection (State: ${TextChatHubService.hubConnection.state})`);
        if (TextChatHubService.hubConnection.state === HubConnectionState.Connected) {
            this.loggerStore.addLog("Exiting because connected");
            return;
        }

        TextChatHubService.hubConnection
            .start()
            .then(() => {
                this.loggerStore.addLog("TextChatHub connected");
                // Only refresh the chat store on re-connect
                if (TextChatHubService.isFirst)
                    TextChatHubService.isFirst = false;
                else {
                    if (GlobalsService.companySettings.useTextChat)
                        this.textChatStore.refresh();
                    this.teamChatStore.refresh();
                }

            })
            .catch(err => {
                //console.error(err);
                setTimeout(() => {
                    this.startConnection();
                }, 5000);
            });
    }

    init() {
        // if (GlobalsService.isDebug)
        //     return;

        TextChatHubService.hubConnection = new HubConnectionBuilder()
            //.configureLogging(LogLevel.Trace)
            .withUrl(GlobalsService.apiUrl + "/textChatHub", { accessTokenFactory: () => GlobalsService.jwtToken })
            .build();

        TextChatHubService.hubConnection.on("AddConversation", (conversation: ITextChatConversationSummaryModel) => this.textChatStore.onAddConversation(conversation));
        TextChatHubService.hubConnection.on("UpdateConversation", (conversation: ITextChatConversationSummaryModel) => this.textChatStore.onUpdateConversation(conversation));
        TextChatHubService.hubConnection.on("CloseConversation", (conversation: ITextChatConversationSummaryModel) => this.textChatStore.onCloseConversation(conversation));
        TextChatHubService.hubConnection.on("ReopenConversation", (conversation: ITextChatConversationSummaryModel) => this.textChatStore.onReopenConversation(conversation));
        TextChatHubService.hubConnection.on("NewMessage", (message: ITextChatMessageModel) => this.textChatStore.onNewMessage(message));
        TextChatHubService.hubConnection.on("UpdateMessageStatus", (message: ITextChatMessageModel) => this.textChatStore.onUpdateMessageStatus(message));

        // Team chat shares this connection.  These only ever arrive for chats the user is a
        // member of -- the server fans them out per-user rather than to the company group.
        TextChatHubService.hubConnection.on("TeamChatNewMessage", (message: ITeamChatMessageModel) => this.teamChatStore.onNewMessage(message));
        TextChatHubService.hubConnection.on("TeamChatUpdateConversation", (conversation: ITeamChatConversationSummaryModel) => this.teamChatStore.onUpdateConversation(conversation));
        TextChatHubService.hubConnection.on("TeamChatRemoveConversation", (teamChatConversationId: number) => this.teamChatStore.onRemoveConversation(teamChatConversationId));

        // The user's own chime settings, changed in another window they have open.  Addressed to this
        // user's connections rather than the company, so it is only ever their own change coming back.
        TextChatHubService.hubConnection.on("TeamChatNotificationSettings", (settings: ITeamChatNotificationSettingsModel) => this.teamChatStore.onNotificationSettings(settings));

        // ...and the same thing going the other way.  Debounced because the volume slider fires on
        // every pixel of a drag, and the other windows only need where it was let go.
        this.teamChatStore.notificationSettingsChanged$
            .pipe(debounceTime(TextChatHubService.NOTIFICATION_SETTINGS_DEBOUNCE_MS))
            .subscribe(settings => this.sendNotificationSettings(settings));

        TextChatHubService.hubConnection.onclose(() => {
            this.loggerStore.addLog("TextChatHub onclose");
            setTimeout(() => {
                this.startConnection();
            }, 10000);
        });

        this.startConnection();
    }

    /**
     * Best effort, and quietly so: this window has already applied the change to itself, and a hub
     * that is down or still connecting is no reason to put an error in front of somebody who moved a
     * volume slider.  The other windows pick the settings up out of local storage on their next load.
     */
    private sendNotificationSettings(settings: ITeamChatNotificationSettingsModel) {
        if (!TextChatHubService.hubConnection || TextChatHubService.hubConnection.state !== HubConnectionState.Connected)
            return;

        TextChatHubService.hubConnection.invoke("TeamChatNotificationSettings", settings).catch(() => { });
    }
}
